import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { customerByPhone, useCallCenter, type Customer } from '../CustomerDemo/callCenterStore';

/** Khung trang con CSKH: tiêu đề + mô tả + nút bên phải */
export function PageHeader({ title, desc, extra }: { title: string; desc: string; extra?: React.ReactNode }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
      <Helmet><title>{title} | Khothi</title></Helmet>
      <div>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
      {extra && <div className="flex flex-wrap items-center gap-2">{extra}</div>}
    </div>
  );
}

export function Kpi({ icon, label, value, hint, tone, onClick, active }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string; tone: string; onClick?: () => void; active?: boolean;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={`text-left bg-white dark:bg-gray-900 border rounded-xl p-4 flex items-center gap-3 shadow-sm transition ${active ? 'border-[#3e91ab] ring-1 ring-[#3e91ab]' : 'border-gray-200 dark:border-gray-800'} ${onClick ? 'cursor-pointer hover:border-[#3e91ab]' : ''}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{value}</div>
        {hint && <div className="text-[11px] text-gray-400 truncate">{hint}</div>}
      </div>
    </Tag>
  );
}

export function Panel({ title, icon, extra, children, className = '' }: { title: string; icon?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm flex flex-col min-w-0 ${className}`}>
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2">{icon}{title}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}

export const detailUrl = (c: Customer | null, phone?: string, extra = '') =>
  c ? `/customer-demo/detail?id=${c.id}${extra}` : `/customer-demo/detail?phone=${encodeURIComponent(phone || '')}`;

/** Ô khách hàng: avatar chữ cái + tên (bấm mở hồ sơ) + số điện thoại */
export function CustomerCell({ customer, phone }: { customer: Customer | null; phone?: string }) {
  const navigate = useNavigate();
  const p = phone || customer?.phones[0] || '';
  return (
    <div className="flex items-center gap-3 py-0.5">
      <div className={`w-8 h-8 rounded-full ${customer?.color || 'bg-gray-400'} text-white flex items-center justify-center font-bold text-sm shrink-0`}>
        {customer ? customer.name.charAt(0).toUpperCase() : '?'}
      </div>
      <div className="min-w-0">
        <button type="button" onClick={() => navigate(detailUrl(customer, p))}
          className="font-semibold text-gray-900 dark:text-gray-100 hover:text-[#3e91ab] cursor-pointer text-left truncate max-w-[170px] block">
          {customer?.name || 'Số máy lạ'}
        </button>
        <div className="text-[13px] text-blue-500">{p}</div>
        {customer?.doNotCall && <span className="text-[10px] font-bold text-red-600 uppercase" title={customer.doNotCall.reason}>⛔ Chặn gọi</span>}
      </div>
    </div>
  );
}

export function CustomerByPhone({ phone }: { phone: string }) {
  const s = useCallCenter();
  return <CustomerCell customer={customerByPhone(s, phone)} phone={phone} />;
}

/** "còn 2 giờ" / "trễ 1 ngày" so với hạn */
export function dueText(at: number, now = Date.now()) {
  const diff = at - now;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.max(1, Math.floor(abs / 60000));
  const span = h >= 24 ? `${Math.floor(h / 24)} ngày` : h >= 1 ? `${h} giờ` : `${m} phút`;
  return diff < 0 ? `trễ ${span}` : `còn ${span}`;
}

export const fmtTime = (t: number, now = Date.now()) => dayjs(t).format(dayjs(t).isSame(now, 'day') ? 'HH:mm [hôm nay]' : 'HH:mm DD/MM/YYYY');

export const Stars = ({ v }: { v?: number }) =>
  typeof v === 'number' && v > 0
    ? <span className={`font-semibold ${v <= 2 ? 'text-rose-600' : v === 3 ? 'text-amber-600' : 'text-emerald-600'}`}>{v}★</span>
    : <span className="text-gray-300">—</span>;
